import React, { useContext, useEffect, useRef, useState } from 'react';

import { GlobalContext } from '../../providers/GlobalProvider';
import { countries } from '../../utils/countriesWithCodes';
import { Check } from './icons';
import {
  createImportProductCategory,
  createImportProduct,
  fetchImportHtsOptions,
  fetchImportProductCategories,
  rankHtsOptions,
} from './lib/import-service';
import type {
  ImportHtsOption,
  ImportProductCategory,
} from './lib/import-service';
import { toast } from './lib/toast';
import {
  checkProductCredits,
  type ProductCreditCheck,
} from './lib/product-credits';
import { ProductCreditsModal } from './ProductCreditsModal';
import { ImportParentProductPicker } from './ImportParentProductPicker';
import type { ImportParentCandidate } from './lib/import-service';
import {
  IMPORT_CATEGORY_DEFAULTS,
  ImportCategorySelect,
} from './ImportCategorySelect';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  Textarea,
} from './ui';

const HIERARCHY_OPTIONS = ['Assembly', 'Sub-Assembly', 'Component'];

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'not_reviewed', label: 'Not Reviewed' },
  { value: 'cleared', label: 'Cleared' },
  { value: 'quarantined', label: 'Quarantined' },
  { value: 'barred', label: 'Barred' },
];

interface FormState {
  name: string;
  hts_code: string;
  category: string;
  hierarchy_type: string;
  description: string;
  traces: string;
  manufacturer: string;
  vendor: string;
  model_number: string;
  specifications: string;
  country_of_origin: string;
  status_flag: string;
  trans_method: string;
  country_of_smelt_pour: string;
  entry_date: string;
  loading_date: string;
  quantity: string;
  unit_of_measure: string;
  product_value: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  hts_code: '',
  category: '',
  hierarchy_type: 'Assembly',
  description: '',
  traces: '',
  manufacturer: '',
  vendor: '',
  model_number: '',
  specifications: '',
  country_of_origin: '',
  status_flag: 'new',
  trans_method: '',
  country_of_smelt_pour: '',
  entry_date: '',
  loading_date: '',
  quantity: '',
  unit_of_measure: '',
  product_value: '',
};

const formatHtsPretty = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  return [
    digits.slice(0, 4),
    digits.slice(4, 6),
    digits.slice(6, 8),
    digits.slice(8, 10),
  ]
    .filter(Boolean)
    .join('.');
};

export const ManualProductWizard: React.FC<{
  open: boolean;
  onOpenChange: (value: boolean) => void;
  databaseId: string;
  databaseName?: string;
  databaseStructure?: 'hierarchy' | 'flat' | 'hts-code-grouping';
  onProductCreated?: (id: string) => void;
  onCreated?: () => void;
}> = ({
  open,
  onOpenChange,
  databaseId,
  databaseName,
  databaseStructure = 'hierarchy',
  onProductCreated,
  onCreated,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<ImportProductCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [htsOptions, setHtsOptions] = useState<ImportHtsOption[]>([]);
  const [parent, setParent] = useState<ImportParentCandidate | null>(null);
  const [htsLoading, setHtsLoading] = useState(false);
  const [htsOpen, setHtsOpen] = useState(false);
  const [validatedHtsCode, setValidatedHtsCode] = useState('');
  const [creditBlock, setCreditBlock] = useState<ProductCreditCheck | null>(
    null,
  );
  const htsRequestRef = useRef(0);

  const htsDigits = form.hts_code.replace(/\D/g, '').slice(0, 10);
  const rankedHtsOptions = rankHtsOptions(htsOptions, htsDigits);
  const groupId = cur_user?.selected_team?.id;
  const categoryOptions = Array.from(
    new Map(
      [...IMPORT_CATEGORY_DEFAULTS, ...categories.map(({ name }) => name)].map(
        (name) => [name.trim().toLowerCase(), name.trim()],
      ),
    ).values(),
  ).filter(Boolean);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError(null);
  };

  useEffect(() => {
    if (!open || !groupId) return;
    let cancelled = false;
    setCategoriesLoading(true);
    fetchImportProductCategories(groupId).then(
      ({ success, categories: nextCategories, error: categoryError }) => {
        if (cancelled) return;
        setCategoriesLoading(false);
        if (success) {
          setCategories(nextCategories);
        } else {
          toast.error(categoryError ?? 'Failed to load product categories');
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [groupId, open]);

  useEffect(() => {
    const requestId = ++htsRequestRef.current;
    if (!open || htsDigits.length < 6) {
      setHtsOptions([]);
      setHtsLoading(false);
      return;
    }

    setHtsLoading(true);
    const timeout = window.setTimeout(() => {
      fetchImportHtsOptions(htsDigits.slice(0, 6), form.country_of_origin).then(
        ({ success, options, error: htsError }) => {
          if (requestId !== htsRequestRef.current) return;
          setHtsLoading(false);
          if (!success) {
            setHtsOptions([]);
            setError(htsError ?? 'Failed to load HTS codes.');
            return;
          }
          setHtsOptions(options);
          if (options.some(({ code }) => code === htsDigits)) {
            setValidatedHtsCode(htsDigits);
          }
        },
      );
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [form.country_of_origin, htsDigits, open]);

  const reset = () => {
    setForm(EMPTY_FORM);
    setParent(null);
    setSaving(false);
    setError(null);
    setHtsOptions([]);
    setHtsLoading(false);
    setHtsOpen(false);
    setValidatedHtsCode('');
    setCreditBlock(null);
  };

  const close = () => {
    onOpenChange(false);
    window.setTimeout(reset, 200);
  };

  const submit = async () => {
    if (saving) return;

    try {
      if (!form.name.trim()) {
        throw new Error('Product name is required.');
      }
      if (htsDigits.length !== 10 || validatedHtsCode !== htsDigits) {
        throw new Error(
          'Select a valid ten-digit HTS code from the search results.',
        );
      }
      const quantity = form.quantity.trim() ? Number(form.quantity) : undefined;
      const productValue = form.product_value.trim()
        ? Number(form.product_value)
        : undefined;
      if (
        quantity !== undefined &&
        (!Number.isFinite(quantity) || quantity < 0)
      ) {
        throw new Error('Quantity must be zero or greater.');
      }
      if (
        productValue !== undefined &&
        (!Number.isFinite(productValue) || productValue < 0)
      ) {
        throw new Error('Unit/Product Value must be zero or greater.');
      }
      if (
        form.entry_date &&
        form.loading_date &&
        new Date(form.loading_date).getTime() >
          new Date(form.entry_date).getTime()
      ) {
        throw new Error('Loading date cannot be later than entry date.');
      }

      setSaving(true);
      setError(null);

      if (groupId) {
        const check = await checkProductCredits(groupId, 1);
        if (!check.ok) {
          setCreditBlock(check);
          return;
        }
      }

      let category = form.category.trim();
      if (category) {
        const existingCategory = categories.find(
          ({ name }) => name.toLowerCase() === category.toLowerCase(),
        );
        if (existingCategory) {
          category = existingCategory.name;
        } else {
          if (!groupId) {
            throw new Error(
              'Select a team before creating a product category.',
            );
          }
          const categoryResult = await createImportProductCategory(
            groupId,
            category,
          );
          if (!categoryResult.success || !categoryResult.category) {
            throw new Error(
              categoryResult.error ?? 'Failed to save product category.',
            );
          }
          category = categoryResult.category.name;
          setCategories((current) =>
            current.some(
              ({ name }) => name.toLowerCase() === category.toLowerCase(),
            )
              ? current
              : [...current, categoryResult.category as ImportProductCategory],
          );
        }
      }

      const fields: { name: string; [key: string]: unknown } = {
        name: form.name.trim(),
        hts_code: htsDigits,
        status_flag: form.status_flag,
      };
      if (databaseStructure === 'hierarchy') {
        fields.hierarchy_type = form.hierarchy_type;
        if (parent && form.hierarchy_type !== 'Assembly') {
          fields.parent = parent.id;
        }
      }
      if (category) fields.category = category;
      const addText = (key: keyof FormState) => {
        const value = form[key].trim();
        if (value) fields[key] = value;
      };
      [
        'description',
        'traces',
        'manufacturer',
        'vendor',
        'model_number',
        'specifications',
        'country_of_origin',
        'trans_method',
        'country_of_smelt_pour',
        'entry_date',
        'loading_date',
        'unit_of_measure',
      ].forEach((key) => addText(key as keyof FormState));
      if (quantity !== undefined) fields.quantity = quantity;
      if (productValue !== undefined) fields.product_value = productValue;

      const result = await createImportProduct(databaseId, fields);
      if (!result.success) {
        throw new Error(result.error ?? 'Failed to create product.');
      }

      toast.success(`${form.name.trim()} added`);
      onCreated?.();
      close();
      if (result.productId) onProductCreated?.(result.productId);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'Failed to create product.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          if (!value) close();
        }}
      >
        <DialogContent
          size='lg'
          className='ih-add-product-dialog'
          onClose={close}
        >
          <DialogHeader>
            <DialogTitle>
              Add product{databaseName ? ` · ${databaseName}` : ''}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              id='ih-add-product-form'
              className='ih-add-product-form'
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
            >
              <FormSection
                title='Product information'
                description='Add the identifying and classification details for this product.'
              >
                <FieldRow label='Name' required>
                  <Input
                    autoFocus
                    value={form.name}
                    onChange={(event) => set('name', event.target.value)}
                    placeholder='Product name'
                    className='ih-add-product-control'
                  />
                </FieldRow>
                <FieldRow label='Model Number (SKU)'>
                  <Input
                    value={form.model_number}
                    onChange={(event) =>
                      set('model_number', event.target.value)
                    }
                    placeholder='Model number or SKU'
                    className='ih-add-product-control'
                  />
                </FieldRow>
                <FieldRow label='Country of Origin'>
                  <CountrySelect
                    value={form.country_of_origin}
                    onChange={(value) => {
                      setValidatedHtsCode('');
                      set('country_of_origin', value);
                    }}
                    placeholder='Select country of origin'
                  />
                </FieldRow>
                <FieldRow label='HTS code' required>
                  <div className='ih-hts-picker'>
                    <Input
                      value={formatHtsPretty(htsDigits)}
                      inputMode='numeric'
                      maxLength={13}
                      role='combobox'
                      aria-expanded={htsOpen && htsOptions.length > 0}
                      aria-controls='ih-add-product-hts-options'
                      onFocus={() => setHtsOpen(true)}
                      onBlur={() =>
                        window.setTimeout(() => setHtsOpen(false), 120)
                      }
                      onChange={(event) => {
                        setValidatedHtsCode('');
                        set(
                          'hts_code',
                          event.target.value.replace(/\D/g, '').slice(0, 10),
                        );
                        setHtsOpen(true);
                      }}
                      placeholder='0000.00.00.00'
                      className='ih-add-product-control ih-mono'
                    />
                    {htsLoading && (
                      <span className='ih-hts-loading'>Searching…</span>
                    )}
                    {htsOpen &&
                      !htsLoading &&
                      htsDigits.length >= 6 &&
                      htsOptions.length > 0 && (
                        <div
                          id='ih-add-product-hts-options'
                          className='ih-hts-options'
                          role='listbox'
                        >
                          {rankedHtsOptions.map((option) => (
                            <button
                              type='button'
                              role='option'
                              aria-selected={option.code === htsDigits}
                              key={option.code}
                              className='ih-hts-option'
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => {
                                set('hts_code', option.code);
                                setValidatedHtsCode(option.code);
                                setHtsOpen(false);
                              }}
                            >
                              <span className='ih-mono'>
                                {formatHtsPretty(option.code)}
                              </span>
                              <span>{option.description || 'HTS code'}</span>
                            </button>
                          ))}
                        </div>
                      )}
                  </div>
                  <div className='ih-add-product-field-help'>
                    Enter at least six digits, then select a ten-digit result.
                  </div>
                </FieldRow>
                <FieldRow label='Category'>
                  <ImportCategorySelect
                    value={form.category}
                    options={categoryOptions}
                    onChange={(value) => set('category', value)}
                    loading={categoriesLoading}
                  />
                </FieldRow>
                {databaseStructure === 'hierarchy' && (
                  <>
                    <FieldRow label='Hierarchy Type'>
                      <Select
                        value={form.hierarchy_type}
                        onValueChange={(value) => {
                          set('hierarchy_type', value);
                          if (
                            parent &&
                            !(
                              value === 'Component' ||
                              (value === 'Sub-Assembly' &&
                                parent.hierarchyType === 'Assembly')
                            )
                          ) {
                            setParent(null);
                          }
                        }}
                        className='ih-add-product-control'
                      >
                        {HIERARCHY_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Select>
                    </FieldRow>
                    {form.hierarchy_type !== 'Assembly' && (
                      <FieldRow label='Parent'>
                        <ImportParentProductPicker
                          databaseId={databaseId}
                          childHierarchyType={form.hierarchy_type}
                          value={parent}
                          onChange={setParent}
                          className='ih-add-product-control'
                        />
                      </FieldRow>
                    )}
                  </>
                )}
                <div className='ih-form-span-2'>
                  <FieldRow label='Description'>
                    <Textarea
                      rows={3}
                      value={form.description}
                      onChange={(event) =>
                        set('description', event.target.value)
                      }
                      placeholder='Brief description'
                      className='ih-add-product-control'
                    />
                  </FieldRow>
                </div>
                <div className='ih-form-span-2'>
                  <FieldRow label='TRACES'>
                    <Textarea
                      rows={3}
                      value={form.traces}
                      onChange={(event) => set('traces', event.target.value)}
                      placeholder='TRACES information'
                      className='ih-add-product-control'
                    />
                  </FieldRow>
                </div>
                <FieldRow label='Manufacturer'>
                  <Input
                    value={form.manufacturer}
                    onChange={(event) =>
                      set('manufacturer', event.target.value)
                    }
                    placeholder='Manufacturer name'
                    className='ih-add-product-control'
                  />
                </FieldRow>
                <FieldRow label='Vendor'>
                  <Input
                    value={form.vendor}
                    onChange={(event) => set('vendor', event.target.value)}
                    placeholder='Vendor name'
                    className='ih-add-product-control'
                  />
                </FieldRow>
                <FieldRow label='Status'>
                  <Select
                    value={form.status_flag}
                    onValueChange={(value) => set('status_flag', value)}
                    className='ih-add-product-control'
                  >
                    {STATUS_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </FieldRow>
                <div className='ih-form-span-2'>
                  <FieldRow label='Specifications'>
                    <Textarea
                      rows={3}
                      value={form.specifications}
                      onChange={(event) =>
                        set('specifications', event.target.value)
                      }
                      placeholder='Technical specifications'
                      className='ih-add-product-control'
                    />
                  </FieldRow>
                </div>
              </FormSection>

              <FormSection
                title='Trade and logistics'
                description='Optional shipment details used by screening and tariff calculations.'
              >
                <FieldRow label='Country of Smelt Pour'>
                  <CountrySelect
                    value={form.country_of_smelt_pour}
                    onChange={(value) => set('country_of_smelt_pour', value)}
                    placeholder='Select country of smelt/pour'
                  />
                </FieldRow>
                <FieldRow label='Transaction Method'>
                  <Input
                    value={form.trans_method}
                    onChange={(event) =>
                      set('trans_method', event.target.value)
                    }
                    placeholder='Transaction method'
                    className='ih-add-product-control'
                  />
                </FieldRow>
                <FieldRow label='Entry Date'>
                  <Input
                    type='date'
                    value={form.entry_date}
                    onChange={(event) => set('entry_date', event.target.value)}
                    className='ih-add-product-control'
                  />
                </FieldRow>
                <FieldRow label='Loading Date'>
                  <Input
                    type='date'
                    value={form.loading_date}
                    onChange={(event) =>
                      set('loading_date', event.target.value)
                    }
                    className='ih-add-product-control'
                  />
                </FieldRow>
                <FieldRow label='Quantity'>
                  <Input
                    type='number'
                    min='0'
                    step='1'
                    value={form.quantity}
                    onChange={(event) => set('quantity', event.target.value)}
                    placeholder='Quantity'
                    className='ih-add-product-control'
                  />
                </FieldRow>
                <FieldRow label='Unit Of Measure'>
                  <Input
                    value={form.unit_of_measure}
                    onChange={(event) =>
                      set('unit_of_measure', event.target.value)
                    }
                    placeholder='Unit of measure'
                    className='ih-add-product-control'
                  />
                </FieldRow>
                <FieldRow label='Unit/Product Value'>
                  <Input
                    type='number'
                    min='0'
                    step='0.01'
                    value={form.product_value}
                    onChange={(event) =>
                      set('product_value', event.target.value)
                    }
                    placeholder='0.00'
                    className='ih-add-product-control'
                  />
                </FieldRow>
              </FormSection>

              {error && (
                <div className='ih-edit-error' role='alert'>
                  {error}
                </div>
              )}
            </form>
          </DialogBody>
          <DialogFooter>
            <Button variant='ghost' onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button
              type='submit'
              form='ih-add-product-form'
              variant='primary'
              disabled={saving}
            >
              <Check size={14} /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ProductCreditsModal
        check={creditBlock}
        teamId={groupId ?? ''}
        onClose={() => setCreditBlock(null)}
      />
    </>
  );
};

const FormSection: React.FC<{
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <section className='ih-add-product-section'>
    <div className='ih-add-product-section-heading'>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
    <div className='ih-form-grid-2'>{children}</div>
  </section>
);

const FieldRow: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => (
  <div className='ih-add-product-field'>
    <Label className='ih-add-product-label'>
      {label}
      {required && <span className='ih-add-product-required'> *</span>}
    </Label>
    {children}
  </div>
);

const CountrySelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => (
  <Select
    value={value}
    onValueChange={onChange}
    className='ih-add-product-control'
  >
    <option value=''>{placeholder}</option>
    {countries.map(({ name, iso2 }) => (
      <option key={iso2} value={iso2}>
        {name} ({iso2})
      </option>
    ))}
  </Select>
);
